import React from 'react'
import { twMerge } from 'tailwind-merge'

import { TrendArrowDownIcon, TrendArrowupIcon } from '../../icons/line'
import { Box } from '../box'
import { Icon } from '../icon'
import { Text } from '../text'
import {
    resolveTrendIcon,
    type TrendDirection,
    type TrendMeasureSize,
    type TrendPreference,
} from './trend'

export interface TrendMeasureProps {
    value?: number | string
    unit?: string
    /** Secondary value shown under the main one (web `subvalue`). */
    subvalue?: number | string
    subunit?: string
    /** Swaps the emphasis between subvalue and subunit. */
    subunitInversed?: boolean
    label?: React.ReactNode
    trend?: TrendDirection
    /** Which direction is the good one. Defaults to `up`. */
    trendPreference?: TrendPreference
    size?: TrendMeasureSize
    /** Placeholder for a missing value — callers pass an already-translated string. */
    emptyValueText?: string
    /**
     * Keeps the label on one line. Opt-in because the platforms genuinely differ:
     * web renders labels in narrow dashboard grid columns and must not wrap them,
     * mobile has always wrapped. Set by the web adapter only.
     */
    labelNoWrap?: boolean
    /**
     * Centre the value alone instead of the value + trend arrow row: a mirror
     * spacer balances the arrow, so a gauge's number sits on the arc's axis
     * whether or not a trend is shown. Off elsewhere — the arrow follows the
     * value in a left-aligned stat.
     */
    balanceTrend?: boolean
    className?: string
    testID?: string
}

// `small` is the mobile widget pair from Figma 9146:150553 — value D2-Regular 36
// (`h1`, 36.49/43.78) with an H1-Regular 18 unit (`heading`). The design system
// has no Light display style, so both stay on the default Regular weight.
const valueSize = { regular: 'display', small: 'h1' } as const
// The regular unit is the D4-Regular content/muted `%` butted against the D1 value
// in the Regularity gauge, Figma Widget/Content 1:8740 (AOS-1361).
const unitSize = { regular: 'D4', small: 'heading' } as const
const unitTone = { regular: 'muted', small: 'default' } as const
const unitGap = { regular: '', small: 'ml-1' } as const
// Arrow is 24px next to the D1 value in Figma 6880:26312; `small` keeps that
// ratio against its D2 value.
const trendIconSize = { regular: 'xl', small: 'sm' } as const
// The same D1 node fixes the arrow's slot: `icon-wrapper` 14988:181189 is
// `items-center pb-8px` with no horizontal gap inside an `items-end` row, so the
// arrow sits at the bottom of the value's line box with the 8px slack below it.
// The mobile D2 pair has no such node, so `small` keeps the placement it shipped
// with until design provides one.
const trendIconSlot = { regular: 'self-end mb-2', small: 'ml-1' } as const
// `balanceTrend` (AOS-1311) re-centres the value by pushing the arrow 12px further
// right, where the gauge arc's inner edge is. Reclaiming that room from the 12px of
// bearing Figma leaves between the digits and the arrow keeps both clear: ~4px
// either side of the glyph. Spacer = icon width + this gap, so the two cannot
// drift apart and the value stays centred whatever the gap is. It overrides only
// the horizontal half of the slot above.
const balancedTrendGap = { regular: '-ml-[5px]', small: '-ml-[3px]' } as const
const balancedSpacerWidth = { regular: 'w-[19px]', small: 'w-[13px]' } as const

/**
 * Big widget measure: value + unit, an optional trend arrow coloured by whether
 * the movement is the preferred one, an optional secondary value, and a caption.
 */
export const TrendMeasure: React.FC<TrendMeasureProps> = ({
    value,
    unit,
    subvalue,
    subunit,
    subunitInversed = false,
    label,
    trend = 'flat',
    trendPreference = 'up',
    size = 'regular',
    emptyValueText = '-',
    labelNoWrap = false,
    balanceTrend = false,
    className,
    testID,
}) => {
    const icon = resolveTrendIcon(trend, trendPreference)
    const hasValue = value !== undefined && value !== null

    return (
        <Box testID={testID} className={twMerge('flex-col', className)}>
            <Box className='flex-row items-baseline'>
                {icon && balanceTrend && (
                    <Box className={twMerge('shrink-0', balancedSpacerWidth[size])} />
                )}
                <Text size={valueSize[size]}>{hasValue ? String(value) : emptyValueText}</Text>
                {unit && (
                    <Text size={unitSize[size]} tone={unitTone[size]} className={unitGap[size]}>
                        {unit}
                    </Text>
                )}
                {icon && (
                    <Icon
                        as={icon.direction === 'up' ? TrendArrowupIcon : TrendArrowDownIcon}
                        size={trendIconSize[size]}
                        className={twMerge(
                            trendIconSlot[size],
                            balanceTrend && balancedTrendGap[size],
                            icon.tone === 'success'
                                ? 'text-status-success'
                                : 'text-status-critical',
                        )}
                    />
                )}
            </Box>
            {subvalue !== undefined && (
                <Box className='mt-2 flex-row'>
                    <Text size='body' tone={subunitInversed ? 'muted' : 'default'}>
                        {String(subvalue)}
                    </Text>
                    {subunit && (
                        <Text
                            size='body'
                            tone={subunitInversed ? 'default' : 'muted'}
                            className='pl-1'
                        >
                            {subunit}
                        </Text>
                    )}
                </Box>
            )}
            {label && (
                <Text
                    size='caption'
                    tone='muted'
                    className={twMerge('mt-2', labelNoWrap && 'whitespace-nowrap')}
                >
                    {label}
                </Text>
            )}
        </Box>
    )
}

TrendMeasure.displayName = 'TrendMeasure'
